import { schema } from 'normalizr';
import isEmpty from '@utils/isEmpty';
import { practiceActionTypes } from '@cont/Practice/constants';
import { practiceSessionTypes } from '../constants';

export const fetchPracticeTopologies = ({
  uid,
  showSubSections = true,
  showAllChildren = false,
  languageCode
}) => ({
  type: practiceSessionTypes.FETCH_PRACTICE_TOPOLOGIES,
  endpoint: `v4/quizzes/practice_session/get_topologies_with_practice_sessions/?show_sub_sections=${
    showSubSections ? 1 : 0
  }&get_all_children=${
    showAllChildren ? 1 : 0
  }&parent_uid=${uid}&language=${languageCode}`,
  method: 'get',
  schema: new schema.Array(),
  processData: (data) => data?.results || [],
  uid
});

export const setLanguagePreference = ({ goalUID, languageCode }) => ({
  type: practiceSessionTypes.SET_LANGUAGE_PREFERENCE,
  endpoint: 'pariksha/v1/practice/language/',
  method: 'post',
  data: {
    goal: goalUID,
    language: languageCode
  },
  goalUID,
  languageCode
});

export const fetchInCorrectPracticeTopologies = (uid) => ({
  type: practiceSessionTypes.FETCH_PRACTICE_TOPOLOGIES,
  endpoint: `v2/quizzes/practice_session/get_incorrect_attempts_topologies/?topic_group=${uid}`,
  method: 'get',
  uid
});

export const fetchPracticeAvailableLanguages = (goalUID) => ({
  type: practiceSessionTypes.FETCH_PRACTICE_AVAILABLE_LANGUAGES,
  endpoint: `pariksha/v2/practice/available_languages/?goal=${goalUID}`,
  method: 'get',
  goalUID
});

export const fetchPYQFilters = (uid) => ({
  type: practiceSessionTypes.FETCH_PRACTICE_PYQ_FILTERS,
  endpoint: `v1/quizzes/practice_session/get_pyq_filter_options/?topic_group=${uid}`,
  method: 'get',
  uid
});

export const fetchPYQTopologies = ({ uid, examUID, startYear, endYear }) => {
  let endpoint = `v1/quizzes/practice_session/get_pyq_topologies/?topic_group=${uid}&exam_uid=${examUID}`;

  if (startYear && endYear) {
    endpoint += `&start_year=${startYear}&end_year=${endYear}`;
  }

  return {
    type: practiceSessionTypes.FETCH_PRACTICE_TOPOLOGIES,
    endpoint,
    method: 'get',
    uid
  };
};

export const createSession = (data) => ({
  type: practiceSessionTypes.CREATE_PRACTICE_SESSION,
  endpoint: 'v3/quizzes/practice_session/create/',
  method: 'post',
  data
});

export const createIncorrectQuestionsSession = (data, practiceType) => ({
  type: practiceSessionTypes.CREATE_PRACTICE_SESSION,
  endpoint:
    'v3/quizzes/practice_session/create_from_previous_practice_incorrect_attempts/',
  method: 'post',
  data,
  practiceType
});

export const fetchSessionDetails = (topicUID) => ({
  type: practiceSessionTypes.FETCH_PRACTICE_SESSION,
  endpoint: `v1/topology/users/${topicUID}/practice_session_details/`,
  method: 'get'
});

export const fetchClassPracticeSessionDetails = (quiz_uid) => ({
  type: practiceSessionTypes.FETCH_CLASS_PRACTICE_SESSION,
  endpoint: 'v3/quizzes/practice_session/start_educator_course_practice/',
  method: 'post',
  data: {
    quiz_uid
  },
  schema: {},
  processData: (data) => {
    return {
      ...data,
      ...data.topic,
      latest_session_uid: data.uid,
      latest_session_attempts: data.latest_session_attempts || [],
      latest_session_available_language: data.available_language,
      latest_session_no_of_questions: data.no_of_questions
    };
  }
});

export const fetchLearnerDetails = (goalUID) => ({
  type: practiceSessionTypes.FETCH_LEARNER_LEVEL_DETAILS,
  endpoint: `v1/gamification/learner/xp/level_details/?topology_uid=${goalUID}`,
  method: 'get',
  goalUID
});

export const getNextQuestion = (sessionId) => ({
  type: practiceSessionTypes.GET_NEXT_PRACTICE_QUESTION,
  endpoint: `v2/quizzes/practice_session/next_question/?uid=${sessionId}`,
  method: 'get'
});

export const submitQuestionResponse = ({
  attemptList,
  hintUsed = false,
  questionUID,
  skipReason = null,
  timeTaken,
  sessionUID
}) => ({
  type: practiceSessionTypes.SUBMIT_PRACTICE_SESSION_ANSWER,
  endpoint: 'v2/quizzes/practice_session/submit_response/',
  method: 'post',
  data: {
    attempt_list: attemptList,
    hint_used: hintUsed,
    question_uid: questionUID,
    skip_reason: skipReason,
    time_taken: timeTaken,
    uid: sessionUID
  },
  isSkipped: !isEmpty(skipReason),
  timeTaken
});

export const finishPracticeSession = (sessionUID) => ({
  type: practiceSessionTypes.FINISH_PRACTICE_SESSION,
  endpoint: 'v1/quizzes/practice_session/finish/',
  method: 'post',
  data: {
    uid: sessionUID
  }
});

export const sendQuestionReview = ({
  questionUID,
  reason,
  review,
  sessionUID
}) => ({
  type: practiceSessionTypes.FINISH_PRACTICE_QUESTION_REVIEW,
  endpoint: 'v1/quizzes/practice_session/review_question/',
  method: 'post',
  data: {
    question_uid: questionUID,
    reason,
    review,
    uid: sessionUID
  },
  review
});

export const fetchPracticeFeedbackOptions = (sessionUID) => ({
  type: practiceSessionTypes.FETCH_PRACTICE_FEEDBACK_OPTIONS,
  endpoint: `v1/feedback/practice_experience/?object_uid=${sessionUID}`,
  method: 'get'
});

export const submitPracticeFeedback = (sessionUID, data) => ({
  type: practiceSessionTypes.SUBMIT_PRACTICE_FEEDBACK,
  endpoint: `v1/feedback/${sessionUID}/response/`,
  method: 'post',
  data
});

export const setPracticeReminderDefaultData = (goalUID, data) => {
  return {
    type: practiceActionTypes.SET_REMINDER_DEFAULT_DATA,
    data: {
      dailyGoal: data.dailyGoal,
      dailyRemainderAt: data.dailyRemainderAt
    },
    goalUID
  };
};

export const fetchPracticeReminderSettings = (goalUID) => ({
  type: practiceSessionTypes.FETCH_PRACTICE_REMINDER,
  endpoint: `v1/quizzes/practice/reminder/info/?goal_uid=${goalUID}`,
  method: 'get',
  goalUID
});

export const fetchPracticeReminderCardInfo = (goalUID) => ({
  type: practiceSessionTypes.FETCH_PRACTICE_REMINDER,
  endpoint: `v1/quizzes/practice/reminder/card-info/?goal_uid=${goalUID}`,
  method: 'get',
  goalUID
});

export const dissmissPracticeReminderCard = (goalUID) => ({
  type: practiceSessionTypes.DISMISS_PRACTICE_REMINDER,
  endpoint: 'v1/quizzes/practice/reminder/dismiss-card/',
  method: 'post',
  data: {
    goal_uid: goalUID
  },
  goalUID
});

export const submitPracticeReminder = (goalUID, data) => ({
  type: practiceSessionTypes.SUBMIT_PRACTICE_REMINDER,
  endpoint: 'v1/quizzes/practice/reminder/save/',
  method: 'post',
  data: {
    ...data,
    goal_uid: goalUID
  },
  goalUID
});

export const deletePracticeReminder = (goalUID) => ({
  type: practiceSessionTypes.DELETE_PRACTICE_REMINDER,
  endpoint: 'v1/quizzes/practice/reminder/delete/',
  method: 'post',
  data: { goal_uid: goalUID },
  goalUID
});

export const setModalState = (data) => ({
  type: practiceSessionTypes.PRACTICE_MODAL_TYPE,
  data
});

export const setSelectedTopic = (data) => ({
  type: practiceSessionTypes.SET_SELECTED_PRACTICE,
  data
});

export const setPracticeAnswer = (data) => ({
  type: practiceSessionTypes.SET_PRACTICE_ANSWERS,
  data
});

export const setPracticeQuestionCount = (data) => ({
  type: practiceSessionTypes.SET_PRACTICE_QUESTION_COUNT,
  data
});

export const setPracticeSeenHint = () => ({
  type: practiceSessionTypes.SET_PRACTICE_SEEN_HINT
});

export const setupNextQuestion = () => ({
  type: practiceSessionTypes.SETUP_NEXT_PRACTICE_QUESTION
});

export const resetPracticeSession = () => ({
  type: practiceSessionTypes.RESET_PRACTICE_SESSION
});
